import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { scheduleDocumentScrollLockRelease } from '../../utils/scrollLock';
import { useDispatch, useSelector } from 'react-redux';
import {
  Search,
  MapPin,
  Menu,
  User,
  ChevronDown,
  ChevronRight,
  Plus,
  X,
} from 'lucide-react';
import { openModal } from '../../stores/slices/commonSlice';
import { logoutAction } from '../../stores/actions/authActions';
import { getCountryList } from '../../stores/actions/eventsActions';
import { setCountry } from '../../stores/slices/eventSlice';
import { useHomeSearch } from '../layouts/layouts';

const MANAGER_BASE = 'https://apsession.com/manager';

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const country = useSelector((state) => state.event.country);
  const getCountryFeeListDetails = useSelector((state) => state.event.getCountryFeeListDetails);
  const homeSearch = useHomeSearch();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [countryMenuOpen, setCountryMenuOpen] = useState(false);
  const [mobileCountryMenuOpen, setMobileCountryMenuOpen] = useState(false);
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [manageEventOpen, setManageEventOpen] = useState(false);
  const userMenuRef = useRef(null);
  const countryMenuRef = useRef(null);
  const mobileCountryMenuRef = useRef(null);

  const countries = Array.isArray(getCountryFeeListDetails) ? getCountryFeeListDetails : [];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.removeProperty('overflow');
    }
    return () => {
      document.body.style.removeProperty('overflow');
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (countryMenuRef.current && !countryMenuRef.current.contains(e.target)) {
        setCountryMenuOpen(false);
      }
      if (mobileCountryMenuRef.current && !mobileCountryMenuRef.current.contains(e.target)) {
        setMobileCountryMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!getCountryFeeListDetails || getCountryFeeListDetails.length === 0) {
      dispatch(getCountryList());
    }
  }, [dispatch, getCountryFeeListDetails]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setUserMenuOpen(false);
    setCountryMenuOpen(false);
    setMobileCountryMenuOpen(false);
    setCreateEventOpen(false);
    setManageEventOpen(false);
    return scheduleDocumentScrollLockRelease();
  }, [location.pathname]);

  const handleSignIn = () => dispatch(openModal());
  const handleLogout = () => {
    setUserMenuOpen(false);
    dispatch(logoutAction()).then((response) => {
      if (response?.meta?.requestStatus === 'fulfilled') navigate('/');
    });
  };
  const goTo = (path) => navigate(path);
  const goToMyTickets = () => {
    if (isLoggedIn) {
      goTo('/my-tickets');
    } else {
      handleSignIn();
    }
  };
  const displayName = user?.username || user?.email || 'Account';
  const avatarInitial = (displayName || '?').charAt(0).toUpperCase();

  const isHomePage = location.pathname === '/';
  const searchBridge = isHomePage ? homeSearch?.searchBridge : null;
  const mobileSearchBridge = searchBridge;
  const showDesktopSearch = Boolean(searchBridge && homeSearch?.showDesktopHeaderSearch);
  const showMobileSearch = Boolean(
    mobileSearchBridge && homeSearch?.showMobileHeaderSearch
  );
  const showMobileEventHeaderTitle = Boolean(
    !showMobileSearch && homeSearch?.showMobileEventHeaderTitle
  );
  const mobileEventHeaderTitle = homeSearch?.mobileEventHeaderTitle || '';
  const showCompactLogo = isScrolled || showMobileSearch || showMobileEventHeaderTitle || showDesktopSearch;

  const handleMobileSearchSubmit = (e) => {
    e.preventDefault();
    searchBridge?.onSearchSubmit?.();
  };

  const handleDesktopSearchSubmit = (e) => {
    e.preventDefault();
    searchBridge?.onSearchSubmit?.();
  };

  const handleCountrySelect = (newCountry) => {
    dispatch(setCountry(newCountry));
    setCountryMenuOpen(false);
    setMobileCountryMenuOpen(false);
  };

  const MobileLink = ({ label, onClick, hideChevron }) => (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between bg-transparent border-none py-4 sm:py-5 text-left group"
      style={{ borderBottom: '1px solid #e5e7eb' }}
    >
      <span className="text-xl sm:text-2xl font-black text-gray-900 group-hover:text-yellow-500 transition-colors">{label}</span>
      {!hideChevron && <ChevronRight size={22} className="sm:w-6 sm:h-6 text-gray-300 group-hover:text-black transition-colors shrink-0" />}
    </button>
  );

  return (
    <>
      <nav className="sticky top-0 z-50 bg-[#ffea00] border-b border-yellow-500 shadow-[0_1px_2px_rgba(0,0,0,0.05),0_-4px_12px_rgba(0,0,0,0.08),0_-2px_6px_rgba(0,0,0,0.04)] transition-all duration-300">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-2 sm:gap-3 h-16 sm:h-20 min-w-0">
            <div
              className="flex-shrink-0 flex items-center cursor-pointer"
              onClick={() => goTo('/')}
            >
              <img
                src={showCompactLogo ? 'https://apsession.s3.us-east-1.amazonaws.com/Apsession+won+icon.png' : 'https://apsession.com/assets/images/logo_new_color_rec.png'}
                alt="Apsession"
                className={`w-auto object-contain drop-shadow-md hover:scale-105 transition-all duration-300 ${showCompactLogo ? 'h-7 sm:h-8' : 'h-8 sm:h-10'}`}
              />
            </div>

            {/* Mobile / tablet center slot: event title or home search */}
            <div
              className={`lg:hidden min-w-0 flex-1 transition-opacity duration-300 ${
                showMobileEventHeaderTitle || showMobileSearch
                  ? 'opacity-100'
                  : 'w-0 flex-none opacity-0 pointer-events-none overflow-hidden'
              }`}
            >
              {showMobileEventHeaderTitle && !showMobileSearch && (
                <p className="m-0 px-1 sm:px-2 text-base sm:text-lg font-bold text-black truncate">
                  {mobileEventHeaderTitle}
                </p>
              )}

              {showMobileSearch && (
                <form onSubmit={handleMobileSearchSubmit} className="min-w-0 w-full">
                  <div className="flex items-center w-full min-w-0 bg-white rounded-full shadow-md p-1 border border-yellow-600/20">
                    <Search size={15} className="text-gray-400 ml-2 flex-shrink-0" />
                    <input
                      type="text"
                      value={mobileSearchBridge?.searchValue ?? ''}
                      onChange={(e) => mobileSearchBridge?.onSearchChange?.(e.target.value)}
                      placeholder="Search events"
                      className="flex-1 min-w-0 bg-transparent border-none focus:ring-0 px-2 text-xs sm:text-sm font-semibold text-gray-900 placeholder-gray-400 outline-none"
                    />
                    <button
                      type="submit"
                      className="bg-black hover:bg-gray-800 text-[#ffea00] flex items-center justify-center px-2.5 sm:px-3 py-1.5 rounded-full text-[11px] sm:text-xs border-none font-black transition-colors shadow-sm ml-0.5 flex-shrink-0"
                    >
                      Search
                    </button>
                  </div>
                </form>
              )}
            </div>

            {showDesktopSearch && (
            <form
              onSubmit={handleDesktopSearchSubmit}
              className="hidden lg:flex flex-1 justify-center px-4 xl:px-8 transition-all duration-300 opacity-100 translate-y-0"
            >
              <div className="flex items-center w-full max-w-lg bg-white rounded-full shadow-md p-1.5 border border-yellow-600/20">
                <Search size={18} className="text-gray-400 ml-3 flex-shrink-0" />
                <input
                  type="text"
                  value={searchBridge?.searchValue ?? ''}
                  onChange={(e) => searchBridge?.onSearchChange?.(e.target.value)}
                  placeholder="Search event name"
                  className="flex-1 bg-transparent border-none focus:ring-0 px-3 text-sm font-semibold text-gray-900 placeholder-gray-400 outline-none w-full min-w-0"
                />
                <div
                  ref={countryMenuRef}
                  className="relative hidden xl:flex items-center border-l border-gray-200 pl-2 pr-1 h-full shrink-0"
                >
                  <button
                    type="button"
                    onClick={() => setCountryMenuOpen((open) => !open)}
                    className="flex items-center max-w-[9rem] rounded-full border-none bg-transparent px-2 py-1.5 hover:bg-gray-50 transition-colors"
                  >
                    <MapPin size={16} className="text-gray-400 mr-1.5 shrink-0" />
                    <span className="text-sm font-bold text-gray-700 truncate">{country || 'Country'}</span>
                    <ChevronDown size={14} className={`ml-1 text-gray-400 shrink-0 transition-transform ${countryMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {countryMenuOpen && countries.length > 0 && (
                    <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-[60] max-h-60 overflow-y-auto">
                      {countries.map((item) => (
                        <button
                          key={item.country}
                          type="button"
                          onClick={() => handleCountrySelect(item.country)}
                          className={`w-full text-left px-4 py-2.5 border-none text-sm font-bold hover:bg-gray-50 transition-colors ${
                            country === item.country ? 'bg-[#ffea00]/20 text-black' : 'bg-white text-gray-700'
                          }`}
                        >
                          {item.country}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  className="bg-black hover:bg-gray-800 text-[#ffea00] flex items-center justify-center px-4 py-2 rounded-full text-sm border-none font-black transition-colors shadow-sm ml-1 shrink-0"
                >
                  Search
                </button>
              </div>
            </form>
            )}

            <div className="hidden lg:flex items-center h-full gap-3 xl:gap-5 flex-shrink-0">
              <div
                className="relative h-full flex items-center"
                onMouseEnter={() => setCreateEventOpen(true)}
                onMouseLeave={() => setCreateEventOpen(false)}
              >
                <a href={`${MANAGER_BASE}/event-setup`} className="text-black hover:text-gray-800 hover:underline decoration-black decoration-4 underline-offset-4 font-bold transition-all flex items-center h-full text-sm xl:text-base whitespace-nowrap">
                  Create Event
                </a>
                {createEventOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-56 z-50">
                    <div className="bg-[#ffea00] shadow-[0_15px_30px_rgba(0,0,0,0.15)] flex flex-col border-t border-yellow-500/20">
                      <a href={`${MANAGER_BASE}/event-setup`} className="flex items-center px-5 py-3 text-black text-base hover:bg-black/10 transition-colors">
                        <Plus size={20} className="mr-3" /> Create Event
                      </a>
                      <a href={`${MANAGER_BASE}/home`} className="block px-5 py-3 text-black text-base hover:bg-black/10 transition-colors">
                        How it works
                      </a>
                      <a href={`${MANAGER_BASE}/pricing`} className="block px-5 py-3 text-black text-base hover:bg-black/10 transition-colors">
                        Pricing
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <div
                className="relative h-full flex items-center"
                onMouseEnter={() => setManageEventOpen(true)}
                onMouseLeave={() => setManageEventOpen(false)}
              >
                <a href={`${MANAGER_BASE}/dashboard/overview`} className="text-black hover:text-gray-800 hover:underline decoration-black decoration-4 underline-offset-4 font-bold transition-all flex items-center h-full text-sm xl:text-base whitespace-nowrap">
                  Manage Event
                </a>
                {manageEventOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-56 z-50">
                    <div className="bg-[#ffea00] shadow-[0_15px_30px_rgba(0,0,0,0.15)] flex flex-col border-t border-yellow-500/20">
                      <a href={`${MANAGER_BASE}/dashboard/overview`} className="block px-5 py-3 text-black text-base hover:bg-black/10 transition-colors">
                        DashBoard
                      </a>
                      <a href={`${MANAGER_BASE}/mobileapp`} className="block px-5 py-3 text-black text-base hover:bg-black/10 transition-colors">
                        Scan Tickets
                      </a>
                      <a href={`${MANAGER_BASE}/help-center`} className="block px-5 py-3 text-black text-base hover:bg-black/10 transition-colors">
                        Supports
                      </a>
                    </div>
                  </div>
                )}
              </div>
              <a href="/my-tickets" onClick={(e) => { e.preventDefault(); goToMyTickets(); }} className="text-black hover:text-gray-800 hover:underline decoration-black decoration-4 underline-offset-4 font-bold transition-all text-sm xl:text-base whitespace-nowrap">My Tickets</a>
              <a href="/lookup" onClick={(e) => { e.preventDefault(); goTo('/lookup'); }} className="hidden xl:inline text-black hover:text-gray-800 hover:underline decoration-black decoration-4 underline-offset-4 font-bold transition-all whitespace-nowrap">Ticket Lookup</a>

              <div className="flex items-center gap-2 xl:gap-4 ml-1 xl:ml-2 border-l border-yellow-600 pl-3 xl:pl-6 h-10">
                {isLoggedIn ? (
                  <div className="relative" ref={userMenuRef}>
                    <button
                      onClick={() => setUserMenuOpen((o) => !o)}
                      className="flex items-center gap-1.5 xl:gap-2 bg-black hover:bg-gray-800 text-white px-3 xl:px-4 py-2.5 rounded-full font-bold transition-colors border-none shadow-md group"
                    >
                      <User size={18} className="text-[#ffea00] shrink-0" />
                      <span className="hidden xl:inline max-w-[120px] truncate">{displayName}</span>
                      <ChevronDown size={16} className={`hidden xl:block transition-transform shrink-0 ${userMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {userMenuOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50">
                        <button onClick={() => { setUserMenuOpen(false); goTo('/my-tickets'); }} className="w-full text-left px-4 py-3 bg-white border-none hover:bg-gray-50 text-gray-800 font-medium">My Tickets</button>
                        <button onClick={() => { setUserMenuOpen(false); goTo('/profile'); }} className="w-full text-left px-4 py-3 bg-white border-none hover:bg-gray-50 text-gray-800 font-medium">Profile</button>
                        <button onClick={() => { setUserMenuOpen(false); goTo('/settings'); }} className="w-full text-left px-4 py-3 bg-white border-none hover:bg-gray-50 text-gray-800 font-medium">Settings</button>
                        <button onClick={handleLogout} className="w-full text-left px-4 py-3 bg-white hover:bg-gray-50 text-red-600 border-none font-semibold border-t border-gray-100">Logout</button>
                      </div>
                    )}
                  </div>
                ) : (
                  <button onClick={handleSignIn} className="flex items-center gap-1.5 xl:gap-2 bg-black hover:bg-gray-800 text-white px-3 xl:px-4 py-2.5 border-none rounded-full font-bold transition-colors shadow-md group text-sm xl:text-base">
                    <User size={18} className="text-[#ffea00] group-hover:scale-110 transition-transform shrink-0" />
                    <span className="hidden xl:inline">Sign In</span>
                  </button>
                )}
              </div>
            </div>

            <div className="lg:hidden flex items-center shrink-0">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="text-black hover:text-gray-800 focus:outline-none p-1.5 sm:p-2 bg-black/5 rounded-xl border-none active:bg-black/10 transition-colors"
                aria-label="Open menu"
              >
                <Menu size={24} className="sm:w-7 sm:h-7" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className={`fixed inset-0 z-[200] bg-white transform transition-transform duration-300 ease-out flex flex-col lg:hidden ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'}`}>
        <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-b border-yellow-500 shadow-sm bg-[#ffea00] shrink-0 h-16 sm:h-20">
          <img src="https://apsession.com/assets/images/logo_new_color_rec.png" alt="Apsession" className="h-8 sm:h-10 w-auto object-contain drop-shadow-md" />
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-1.5 sm:p-2 bg-black/10 hover:bg-black/20 rounded-full border-none transition-colors text-black"
            aria-label="Close menu"
          >
            <X size={24} className="sm:w-7 sm:h-7" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 flex flex-col bg-white">
          <div
            ref={mobileCountryMenuRef}
            className="relative flex justify-center mb-6 sm:mb-10"
          >
            <button
              type="button"
              onClick={() => setMobileCountryMenuOpen((open) => !open)}
              className="flex items-center justify-center w-max max-w-full bg-gray-50 px-4 py-2 rounded-full border border-gray-200 shadow-sm"
            >
              <MapPin size={14} className="text-gray-400 mr-2 shrink-0" />
              <span className="font-bold text-sm text-gray-700 truncate">{country || 'Country'}</span>
              <ChevronDown size={16} className={`ml-1 text-gray-400 shrink-0 transition-transform ${mobileCountryMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileCountryMenuOpen && countries.length > 0 && (
              <div className="absolute top-full left-1/2 z-10 mt-2 w-56 max-h-60 -translate-x-1/2 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                {countries.map((item) => (
                  <button
                    key={item.country}
                    type="button"
                    onClick={() => handleCountrySelect(item.country)}
                    className={`w-full border-none px-4 py-3 text-left text-sm font-bold transition-colors hover:bg-[#ffea00]/20 ${
                      country === item.country ? 'bg-[#ffea00]/30 text-black' : 'bg-white text-gray-700'
                    }`}
                  >
                    {item.country}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative mx-auto mb-6 sm:mb-8 flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-[#ffea00] shadow-lg sm:h-32 sm:w-32">
            {isLoggedIn ? (
              <>
                <span className="absolute inset-0 flex items-center justify-center text-3xl font-black text-black select-none sm:text-4xl">
                  {avatarInitial}
                </span>
                {user?.image ? (
                  <img
                    src={user.image}
                    alt={displayName}
                    className="relative z-10 h-full w-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : null}
              </>
            ) : (
              <User size={44} strokeWidth={2} className="text-black/50 sm:h-16 sm:w-16" />
            )}
          </div>

          {isLoggedIn ? (
            <>
              <div className="text-center mb-4 px-2">
                <p className="text-sm text-gray-500 font-bold">Signed in as</p>
                <p className="text-base sm:text-lg font-black text-gray-900 truncate">{displayName}</p>
              </div>
              <button
                onClick={() => { setIsMobileMenuOpen(false); handleLogout(); }}
                className="w-full bg-black hover:bg-gray-900 text-white font-black text-lg sm:text-xl py-3.5 sm:py-4 rounded-2xl mb-8 sm:mb-12 shadow-lg transition-transform active:scale-95 border-none"
              >
                Logout
              </button>
            </>
          ) : (
            <button
              onClick={() => { setIsMobileMenuOpen(false); handleSignIn(); }}
              className="w-full bg-[#ffea00] hover:bg-[#e6d300] text-black font-black text-lg sm:text-xl py-3.5 sm:py-4 border-none rounded-2xl mb-8 sm:mb-12 shadow-lg transition-transform active:scale-95"
            >
              Sign In
            </button>
          )}

          <div className="flex flex-col flex-1 mb-6 sm:mb-8">
            <MobileLink label="Browse Events" hideChevron onClick={() => { goTo('/'); setIsMobileMenuOpen(false); }} />
            <MobileLink label="Create Events" onClick={() => { setIsMobileMenuOpen(false); window.location.href = `${MANAGER_BASE}/event-setup`; }} />
            <MobileLink label="How it works" onClick={() => { setIsMobileMenuOpen(false); window.location.href = `${MANAGER_BASE}/home`; }} />
            <MobileLink label="Pricing" onClick={() => { setIsMobileMenuOpen(false); window.location.href = `${MANAGER_BASE}/pricing`; }} />
            <MobileLink label="Ticket Lookup" onClick={() => { goTo('/lookup'); setIsMobileMenuOpen(false); }} />
            <MobileLink label="My Tickets" onClick={() => { setIsMobileMenuOpen(false); goToMyTickets(); }} />
            {isLoggedIn && (
              <>
                <MobileLink label="Profile" onClick={() => { goTo('/profile'); setIsMobileMenuOpen(false); }} />
                <MobileLink label="Settings" onClick={() => { goTo('/settings'); setIsMobileMenuOpen(false); }} />
              </>
            )}
          </div>

          <div className="mt-auto text-center pb-6 sm:pb-8 pt-6 sm:pt-8 border-t border-gray-100">
            <p className="text-gray-500 font-bold mb-4 text-sm">Download the mobile App!</p>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 px-2">
              <a href="https://apps.apple.com/us/app/apsession/id802888811" target="_blank" rel="noopener noreferrer">
                <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="App Store" className="h-9 sm:h-10 object-contain" />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.apsession.air.apsessionuser" target="_blank" rel="noopener noreferrer">
                <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" className="h-9 sm:h-10 object-contain" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
